import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {CS_TREND_BY_COUNTRY_COLORS, POM_TREND_BY_COUNTRY_COLORS, } from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';
import {CountryChartInfo} from '../../../../models/country-chart-info.interface';


interface YearCountryData {
    year: string;
    countryData: CountryChartData[];
}

@Component({
    selector: 'app-trend-by-country-bar-chart',
    templateUrl: './trend-by-country-bar-chart.component.html',
    styleUrls: ['./trend-by-country-bar-chart.component.scss']
})
export class TrendByCountryBarChartComponent implements OnInit, OnChanges {

    @Input()
    data!: [CountryChartData[]];
    @Input()
    displayUnits!: string;
    @Input()
    title!: string;
    @Input()
    subtitle!: string;
    @Input()
    countryCategory!: string;
    @Input()
    countries!: CountryChartInfo[];

    textBelow!: string;
    chart!: Chart;
    chartData!: SeriesOptionsType[];
    countryCodes!: string[];
    textBelowCodePOM = 'kfcharts.Country.PutOnMarket.TrendByCountry.textBelow';
    textBelowCodeCollected = 'kfcharts.Country.Collected.TrendByCountry.textBelow';
    isAdmin!: boolean;

    constructor(private readonly messageService: MessageService,
                private readonly router: Router,
                private authService: AuthService) {
    }

    ngOnInit(): void {
        this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
        this.messageService.getDialogInfo(
            this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected).subscribe((data) => {
            if (data !== null) {
                this.textBelow = data.description;
            }
        });
    }

    ngOnChanges(): void {
        if (this.data) {
            if (this.data.length) {
                this.data[0].sort((a, b) => {
                    if (a.countryCode < b.countryCode) {
                        return -1;
                    }
                    if (a.countryCode > b.countryCode) {
                        return 1;
                    }
                    return 0;
                });
            }
            this.countryCodes = this.countries.map(c => c.code);
            const normalizedData = this.normalizeData(this.groupByYear(this.data), this.countries);

            // highcharts
            this.chartData = this.convertToChartData(normalizedData);
            this.chart = new Chart({
                chart: {
                    zoomType: 'xy',
                    type: 'column',
                    options3d: {
                        enabled: true,
                        alpha: 7,
                        beta: 0,
                        depth: 35,
                        viewDistance: 15
                    }
                },
                colors: this.countryCategory === 'putonmarket' ? POM_TREND_BY_COUNTRY_COLORS : CS_TREND_BY_COUNTRY_COLORS,
                title: {
                    text: this.title
                },
                subtitle: {
                    text: this.subtitle
                },
                exporting: {
                    tableCaption: this.title + ' ' + this.subtitle
                },
                tooltip: {
                    formatter(): string[] | undefined {
                        if (this.point.name) {
                            const pointOptions = this.point as PointOptionsObject;
                            if (pointOptions.custom?.absolute === 0) {
                                return [this.point.name];
                            }
                            return ['<div class="center"><b>' + pointOptions.custom?.title + '</b></div><br><br>',
                                '- Absolute: ' + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>',
                                '- Population: ' + (pointOptions.custom?.population ? formatNumber(pointOptions.custom?.population) + ' inhabitants' : 'No data') + '<br>',
                                '- Specific: ' + (pointOptions.custom?.population
                                    ? formatNumber(pointOptions.custom?.absolute * 1000 / pointOptions.custom?.population) + ' kg/inh.'
                                    : 'No data') + '<br>',
                            ];
                        }
                        // If not null, use the default formatter
                        // return tooltip.defaultFormatter.call(this, tooltip);
                        return undefined;
                    }
                },
                xAxis: {
                    categories: this.countryCodes,
                    title: {
                        text: 'Country',
                        margin: 0,
                      style: {
                          height: 0,
                          opacity: 0
                      }
                    }
                },
                plotOptions: {
                    column: {
                        minPointLength: 0.5
                    }
                },
                series: this.chartData
            });
        }
    }

    editTextBelow(): void {
        this.router.navigate(
            ['/admin/chart-labels'],
            {state: {code: this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected}}).then(
            () => {
                // do nothing
            }
        );
    }

    private normalizeData(groupedByYear: YearCountryData[], countryCodes: CountryChartInfo[]): YearCountryData[] {
        const normalizedData: YearCountryData[] = [];
        groupedByYear.forEach(item => {
            if (item.countryData?.length === countryCodes?.length) {
                normalizedData.push(item);
            } else {
                const newCountryData: CountryChartData[] = [];
                countryCodes?.forEach(countryInfo => {
                    let countryDataItem = item.countryData.find(c => c.countryCode === countryInfo.code);
                    if (!countryDataItem) {
                        countryDataItem = {
                            countryCode: countryInfo.code,
                            absolute: 0,
                            year: Number(item.year),
                            population: 0,
                            countryName: countryInfo.name
                        } as CountryChartData;
                    }
                    newCountryData.push(countryDataItem);
                });
                normalizedData.push({year: item.year, countryData: newCountryData});
            }
        });
        return normalizedData;
    }

    private convertToChartData(data: YearCountryData[]): SeriesOptionsType[] {
        this.chart?.ref?.hideData();
        const chartData = [] as SeriesOptionsType[];
        data.forEach(item => {
            chartData.push({
                name: item.year,
                type: 'column',
                data: item.countryData.map(dataItem => {
                    return {
                        y: (this.displayUnits === 'SPECIFIC' ?
                            (dataItem.population ? Number(formatNumber((dataItem.absolute * 1000 / dataItem.population))) : 0) :
                            dataItem.absolute),
                        name: dataItem.countryName,
                        custom: {
                          population: dataItem.population,
                          absolute: dataItem.absolute,
                          title: (dataItem.countryName || 'No data available') + ' - ' + dataItem.year
                        }
                    };
                })

            });
        });
        return chartData;
    }

    private groupByYear(objectArray: Array<any>): YearCountryData[] {
        const yearCountryDataArray: YearCountryData[] = [];
        const groupedArray = objectArray.reduce((acc, obj) => {
            const key = obj[0].year;
            if (!acc[key]) {
                acc[key] = [];
            }
            // Add object to list for given key's value
            obj.forEach((item: any) => acc[key].push(item));
            return acc;
        }, {});

        Object.getOwnPropertyNames(groupedArray).forEach((key: string) => {
            const yearCountryData = {} as YearCountryData;
            yearCountryData.year = key;
            yearCountryData.countryData = groupedArray[key];
            yearCountryDataArray.push(yearCountryData);
        });

        return yearCountryDataArray;
    }
}
